import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Box, Rect } from 'leafer-ui';
import { Spin } from 'antd';
import stores from '@stores';
import { computedSize, getPosition, getMargin } from '@utils/utils';
import macosIcon from '@utils/macosIcon';
import { windowDark, windowLight } from '@utils/windowsIcon';
import deviceFrames from '@utils/deviceFrames';
import { debounce } from 'lodash';

// 使用占位图替代缺失资源
const placeholder = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

// 获取设备框架配置函数
const getDeviceFrameConfig = (frameId) => {
    // 遍历所有设备分类查找匹配的框架
    for (const category in deviceFrames) {
        if (deviceFrames[category]?.items) {
            const foundItem = deviceFrames[category].items.find(item => item.id === frameId);
            if (foundItem) return foundItem;
        }
    }
    return null;
};

const info = {
    macbookpro16: {
        image: placeholder,
        width: 1920,
        height: 1266,
        horizontal: 0.85,
        vertical: 0.85,
        top: 0.09
    },
    macbookair: {
        image: placeholder,
        width: 1920,
        height: 1147,
        horizontal: 0.83,
        vertical: 0.88,
        top: 0.02
    },
    imacpro: {
        image: placeholder,
        width: 1920,
        height: 1599,
        horizontal: 0.9,
        vertical: 0.64,
        top: 0.06
    },
    ipadpro: {
        image: placeholder,
        width: 1920,
        height: 1425,
        horizontal: 0.88,
        vertical: 0.83,
        top: 0.08
    },
    iphonepro: {
        image: placeholder,
        width: 968,
        height: 1920,
        horizontal: 0.88,
        vertical: 0.93,
        top: 0.035
    }
};

const createSnap = debounce(() => {
    stores.editor.createSnap('update');
}, 100);

export default observer(({ parent }) => {
    const { t } = useTranslation('editor');
    const bar = useRef(null);
    const filteredImageCache = useRef(null);
    const [loading, setLoading] = useState(false);
    // 添加一个引用来存储所有装饰元素
    const decorationElements = useRef([]);
    
    const [image, box, container] = useMemo(() => {
        const image = new Rect({
            origin: 'center'
        });
        const box = new Box({
            overflow: 'hide',
            children: [image]
        });
        const container = new Box({
            id: 'screenshot-box',
            overflow: 'hide',
            strokeAlign: 'outside',
            scale: 1,
            fill: '#ffffff00',
            children: [box]
        });
        return [image, box, container];
    }, [parent]);

    // 使用Canvas应用滤镜并返回处理后的图像URL
    const applyFilterToImage = async (imageUrl, filterString) => {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                
                // 应用滤镜
                ctx.filter = filterString;
                ctx.drawImage(img, 0, 0);
                
                // 返回处理后的图像URL
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = reject;
            img.src = imageUrl;
        });
    };

    // 更新图像源
    const updateImageSource = async () => {
        const imageUrl = stores.editor.img.src;
        const shouldApplyFilter = stores.option.enableFilter && stores.option.filterString;
        
        if (!imageUrl) return;
        
        try {
            setLoading(true);
            
            // 获取图片的自然尺寸，以便确定正确的渲染模式
            const getImageDimensions = (url) => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.onload = () => resolve({ width: img.width, height: img.height });
                    img.onerror = reject;
                    img.src = url;
                });
            };
            
            // 获取图片比例
            const dimensions = await getImageDimensions(imageUrl);
            const imgAspectRatio = dimensions.width / dimensions.height;
            
            // 智能选择对齐方式和渲染模式
            const align = stores.option.mode === 'fit' ? 'center' : 'center';
            
            // 如果需要应用滤镜
            if (shouldApplyFilter) {
                // 应用滤镜并更新图像
                const filteredUrl = await applyFilterToImage(imageUrl, stores.option.filterString);
                filteredImageCache.current = filteredUrl;
                
                image.fill = {
                    type: 'image',
                    url: filteredUrl,
                    align: align,
                    mode: stores.option.mode
                };
            } else {
                // 不应用滤镜，使用原始图像
                filteredImageCache.current = null;
                
                image.fill = {
                    type: 'image',
                    url: imageUrl,
                    align: align,
                    mode: stores.option.mode
                };
            }
            
            createSnap();
        } catch (error) {
            console.error(t('error.applyFilter'), error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        updateImageSource();
    }, [stores.option.mode, stores.editor.img.src]);

    useEffect(() => {
        if (stores.option.enableFilter && stores.option.filterString) {
            updateImageSource();
        } else if (filteredImageCache.current) {
            // 当禁用滤镜时，恢复原始图像
            image.fill = {
                type: 'image',
                url: stores.editor.img.src,
                align: stores.option.mode === 'fit' ? 'center' : 'top',
                mode: stores.option.mode
            };
            filteredImageCache.current = null;
            createSnap();
        }
    }, [stores.option.enableFilter, stores.option.filterString]);

    useEffect(() => {
        if (stores.option.padding === 0 && !info[stores.option.frame]) {
            box.fill = '#ffffff00';
        } else {
            box.fill = stores.option.paddingBg;
        }
        createSnap();
    }, [stores.option.paddingBg, stores.option.padding]);

    useEffect(() => {
        const { round } = stores.option;
        container.cornerRadius = round;
        if (!bar.current || info[stores.option.frame]) {
            image.cornerRadius = round;
        }
        createSnap();
    }, [stores.option.round]);

    useEffect(() => {
        const { shadow } = stores.option;
        if (shadow === 0 || stores.option.frame === 'macbookpro16') {
            container.shadow = null;
        } else {
            container.shadow = {
                x: shadow * 4,
                y: shadow * 4,
                blur: shadow * 3,
                color: '#00000045',
                box: true
            };
        }
        createSnap();
    }, [stores.option.shadow]);

    useEffect(() => {
        container.scale = stores.option.scale;
        createSnap();
    }, [stores.option.scale]);

    useEffect(() => {
        if (!filteredImageCache.current) {
            image.url = stores.editor.img.src;
        }
    }, [stores.editor.img.src]);

    useEffect(() => {
        image.scaleX = stores.option.scaleX ? -1 : 1;
        createSnap();
    }, [stores.option.scaleX]);

    useEffect(() => {
        image.scaleY = stores.option.scaleY ? -1 : 1;
        createSnap();
    }, [stores.option.scaleY]);

    useEffect(() => {
        image.rotation = stores.option.rotation;
        createSnap();
    }, [stores.option.rotation]);

    // 添加一个清理装饰元素的辅助函数
    const clearDecorations = () => {
        // 移除所有装饰元素
        if (decorationElements.current.length > 0) {
            decorationElements.current.forEach(elem => {
                if (elem && elem.remove) {
                    elem.remove();
                }
            });
            // 清空数组
            decorationElements.current = [];
        }
    };

    useEffect(() => {
        const { align, frame, frameConf, shadow, round, padding } = stores.option;
        const { img } = stores.editor;
        const margin = getMargin(frameConf.width, frameConf.height);
        let { width, height } = computedSize(img.width, img.height, frameConf.width - margin, frameConf.height - margin);
        let totalHeight = height;
        let boxX = 0;
        let boxY = 0;
        let boxWidth = width;
        let boxHeight = height;
        
        // 清理之前的所有装饰元素
        if (bar.current) {
            bar.current.remove();
            bar.current = null;
        }
        // 使用辅助函数清理所有装饰元素
        clearDecorations();
        
        // 处理不同类型的框架
        switch (frame) {
            case 'light':
                container.strokeWidth = 8;
                container.stroke = '#ffffff80';
                break;
            case 'dark':
                container.strokeWidth = 8;
                container.stroke = '#00000050';
                break;
            case 'macosBarLight':
            case 'macosBarDark':
            case 'windowsBarLight':
            case 'windowsBarDark':
                totalHeight += 32;
                boxY = 32;
                const barUrl = {
                    mac: { type: 'image', url: macosIcon, format: 'svg', mode: 'clip', offset: { x: 10, y: 0 } },
                    windowsBarLight: { type: 'image', url: windowDark, format: 'svg', mode: 'clip', offset: { x: width - 105, y: 0 } },
                    windowsBarDark: { type: 'image', url: windowLight, format: 'svg', mode: 'clip', offset: { x: width - 105, y: 0 } },
                }
                bar.current = new Rect({
                    x: 0,
                    y: 0,
                    height: 32,
                    width: width,
                    fill: [
                        { type: 'solid', color: frame.includes('Dark')? '#3a3a3b' : '#ffffff' },
                        barUrl[frame] || barUrl.mac
                    ]
                });
                container.addAfter(bar.current, box);
                box.cornerRadius = null;
                image.cornerRadius = null;
                break;
            case 'macbookpro16':
            case 'macbookair':
            case 'imacpro':
            case 'ipadpro':
            case 'iphonepro':
                const device = info[frame];
                const bgSize = computedSize(device.width, device.height, width, height);
                bar.current = new Rect({
                    x: 0,
                    y: 0,
                    height,
                    width,
                    fill: [
                        {
                            type: 'image', url: device.image, align: 'center', mode: 'contain',
                            size: {
                                width: width,
                                height: height
                            }
                        },
                    ]
                });
                boxWidth = bgSize.width * device.horizontal;
                boxHeight = bgSize.height * device.vertical;
                boxX = (width - boxWidth) / 2;
                boxY = (bgSize.height * device.top) + (height - bgSize.height) / 2;
                container.shadow = null;
                box.cornerRadius = frame === 'iphonepro' ? bgSize.width * 1/10 : null;
                container.addAfter(bar.current, box);
                break;
            case 'lightGray':
                container.strokeWidth = 8;
                container.stroke = '#f5f5f580';
                break;
            case 'darkGray':
                container.strokeWidth = 8;
                container.stroke = '#33333380';
                break;
            case 'coloredBorder':
                container.strokeWidth = 8;
                container.stroke = '#ff5f6d80';
                break;
            case 'gradientBorder':
                container.strokeWidth = 8;
                container.stroke = '#a855f780';
                break;
            case 'roundedCorners':
                container.strokeWidth = null;
                container.stroke = null;
                container.cornerRadius = 20;
                break;
            case 'shadowed':
                container.strokeWidth = null;
                container.stroke = null;
                container.shadow = {
                    x: 10,
                    y: 10,
                    blur: 15,
                    color: '#0000004d',
                    box: true
                };
                break;
            case 'dottedBorder':
                // 移除虚线效果，改为使用实际的波点装饰
                container.strokeWidth = null;
                container.stroke = null;
                container.fill = '#ffffff'; // 确保背景色
                container.cornerRadius = 20; // 添加圆角效果
                
                // 创建波点装饰
                const dots = [];
                const dotColors = ['#f9a8d4', '#f472b6', '#fbcfe8'];
                const dotDensity = 20; // 点的密度
                const margin = 15; // 边缘间距
                
                // 计算四边的波点位置
                // 上边
                for (let i = margin; i < width - margin; i += width / dotDensity) {
                    dots.push({
                        x: i,
                        y: -5,
                        size: 10 + Math.random() * 6,
                        color: dotColors[Math.floor(Math.random() * dotColors.length)],
                        opacity: 0.7 + Math.random() * 0.3
                    });
                }
                
                // 下边
                for (let i = margin; i < width - margin; i += width / dotDensity) {
                    dots.push({
                        x: i,
                        y: height - 5,
                        size: 10 + Math.random() * 6,
                        color: dotColors[Math.floor(Math.random() * dotColors.length)],
                        opacity: 0.7 + Math.random() * 0.3
                    });
                }
                
                // 左边
                for (let i = margin; i < height - margin; i += height / dotDensity) {
                    dots.push({
                        x: -5,
                        y: i,
                        size: 10 + Math.random() * 6,
                        color: dotColors[Math.floor(Math.random() * dotColors.length)],
                        opacity: 0.7 + Math.random() * 0.3
                    });
                }
                
                // 右边
                for (let i = margin; i < height - margin; i += height / dotDensity) {
                    dots.push({
                        x: width - 5,
                        y: i,
                        size: 10 + Math.random() * 6,
                        color: dotColors[Math.floor(Math.random() * dotColors.length)],
                        opacity: 0.7 + Math.random() * 0.3
                    });
                }
                
                // 添加四个角的特殊大点
                const cornerDots = [
                    { x: -8, y: -8, size: 16 },
                    { x: width - 8, y: -8, size: 16 },
                    { x: -8, y: height - 8, size: 16 },
                    { x: width - 8, y: height - 8, size: 16 }
                ];
                
                cornerDots.forEach(dot => {
                    dots.push({
                        ...dot,
                        color: '#f472b6',
                        opacity: 0.9
                    });
                });
                
                dots.forEach((dot, index) => {
                    const dotElem = new Rect({
                        x: dot.x,
                        y: dot.y,
                        width: dot.size,
                        height: dot.size,
                        cornerRadius: dot.size / 2, // 圆形波点
                        fill: `${dot.color}${Math.floor(dot.opacity * 255).toString(16).padStart(2, '0')}`,
                        shadowColor: dot.color,
                        shadowBlur: 3,
                        shadowOpacity: 0.3
                    });
                    
                    // 将元素添加到装饰数组中
                    decorationElements.current.push(dotElem);
                    
                    if (index === 0) {
                        bar.current = dotElem;
                        container.addAfter(bar.current, box);
                    } else {
                        container.addAfter(dotElem, box);
                    }
                });
                
                // 调整内容区域
                boxWidth = width * 0.85;
                boxHeight = height * 0.85;
                boxX = (width - boxWidth) / 2;
                boxY = (height - boxHeight) / 2;
                break;
            case 'heartDecorated':
                container.strokeWidth = null;
                container.stroke = null;
                container.fill = '#ffffff'; // 确保背景色
                container.cornerRadius = 20; // 添加圆角效果
                
                // 添加心形边框背景
                const heartBorder = new Rect({
                        x: 0,
                        y: 0,
                    width: width,
                    height: height,
                    cornerRadius: 20,
                    stroke: '#f87171a0',
                    strokeWidth: 8
                });
                bar.current = heartBorder;
                container.addAfter(bar.current, box);
                
                // 添加更生动、更有层次的爱心装饰
                const hearts = [
                    // 四角大爱心
                    { x: -12, y: -12, size: 36, color: '#f87171', rotation: -15, glow: true },
                    { x: width - 24, y: -12, size: 36, color: '#f87171', rotation: 15, glow: true },
                    { x: -12, y: height - 24, size: 36, color: '#f87171', rotation: -15, glow: true },
                    { x: width - 24, y: height - 24, size: 36, color: '#f87171', rotation: 15, glow: true },
                    
                    // 边缘装饰爱心
                    { x: width / 2 - 10, y: -18, size: 28, color: '#f87171', rotation: 0, glow: true },
                    { x: width / 2 - 10, y: height - 10, size: 28, color: '#f87171', rotation: 0, glow: true },
                    { x: -15, y: height / 2 - 10, size: 28, color: '#f87171', rotation: 0, glow: true },
                    { x: width - 15, y: height / 2 - 10, size: 28, color: '#f87171', rotation: 0, glow: true },
                    
                    // 添加更多小爱心装饰
                    { x: width / 4, y: -10, size: 20, color: '#fda4af', rotation: 10 },
                    { x: width * 3/4, y: -10, size: 20, color: '#fda4af', rotation: -10 },
                    { x: width / 4, y: height - 10, size: 20, color: '#fda4af', rotation: 10 },
                    { x: width * 3/4, y: height - 10, size: 20, color: '#fda4af', rotation: -10 },
                    { x: -10, y: height / 4, size: 20, color: '#fda4af', rotation: 10 },
                    { x: -10, y: height * 3/4, size: 20, color: '#fda4af', rotation: -10 },
                    { x: width - 10, y: height / 4, size: 20, color: '#fda4af', rotation: 10 },
                    { x: width - 10, y: height * 3/4, size: 20, color: '#fda4af', rotation: -10 },
                    
                    // 随机分布的小爱心
                    ...Array.from({ length: 8 }, () => ({
                        x: Math.random() * width,
                        y: Math.random() < 0.5 ? -5 - Math.random() * 10 : height + Math.random() * 10,
                        size: 12 + Math.random() * 10,
                        color: Math.random() < 0.7 ? '#fda4af' : '#f87171',
                        rotation: Math.random() * 40 - 20
                    }))
                ];
                
                hearts.forEach(heart => {
                    const heartElem = new Rect({
                        x: heart.x,
                        y: heart.y,
                        width: heart.size,
                        height: heart.size,
                        fill: 'transparent',
                        cornerRadius: 0,
                        rotation: heart.rotation,
                        text: {
                            text: '❤',
                            fill: heart.color,
                            fontSize: heart.size,
                            align: 'center',
                            baseline: 'middle'
                        }
                    });
                    
                    // 添加发光效果
                    if (heart.glow) {
                        heartElem.shadowColor = heart.color;
                        heartElem.shadowBlur = 10;
                        heartElem.shadowOpacity = 0.5;
                    }
                    
                    // 将元素添加到装饰数组中
                    decorationElements.current.push(heartElem);
                    container.addAfter(heartElem, box);
                });
                
                // 扩大间距
                boxWidth = width * 0.82;
                boxHeight = height * 0.82;
                boxX = (width - boxWidth) / 2;
                boxY = (height - boxHeight) / 2;
                break;
            case 'bubbleFrame':
                container.cornerRadius = 36; // 增加圆角
                container.fill = '#ffffff'; // 确保背景色
                container.strokeWidth = 7;
                container.stroke = '#88c6fd90';
                
                // 更明显的气泡尾巴
                const tail = new Rect({
                    x: width / 7,
                    y: height - 5,
                    width: 35, // 更大的尾巴
                    height: 35,
                    cornerRadius: 10,
                    rotation: 45,
                    fill: container.fill || '#ffffff',
                    stroke: '#88c6fd90',
                    strokeWidth: 7
                });
                bar.current = tail;
                container.addAfter(tail, box);
                
                // 添加更多、更明显的气泡装饰
                const bubbles = [
                    { x: width - 40, y: 30, size: 25, opacity: 0.9 },
                    { x: width - 65, y: 55, size: 18, opacity: 0.8 },
                    { x: width - 85, y: 35, size: 12, opacity: 0.7 },
                    { x: width - 30, y: 70, size: 15, opacity: 0.8 },
                    { x: width - 100, y: 50, size: 20, opacity: 0.7 },
                    { x: width / 3, y: -5, size: 16, opacity: 0.8 }
                ];
                
                bubbles.forEach(bubble => {
                    const bubbleElem = new Rect({
                        x: bubble.x,
                        y: bubble.y,
                        width: bubble.size,
                        height: bubble.size,
                        cornerRadius: bubble.size / 2,
                        fill: `rgba(136, 198, 253, ${bubble.opacity * 0.3})`,
                        stroke: `rgba(136, 198, 253, ${bubble.opacity * 0.5})`,
                        strokeWidth: Math.max(2, bubble.size / 6)
                    });
                    // 将元素添加到装饰数组中
                    decorationElements.current.push(bubbleElem);
                    container.addAfter(bubbleElem, box);
                });
                
                // 调整内容区域
                boxWidth = width * 0.80;
                boxHeight = height * 0.78;
                boxX = (width - boxWidth) / 2;
                boxY = (height - boxHeight) / 2 - 8; // 向上移动，为底部的尾巴腾出更多空间
                break;
            case 'starDecorated':
                container.strokeWidth = null;
                container.stroke = null;
                container.fill = '#ffffff'; // 确保背景色
                container.cornerRadius = 20; // 添加圆角效果
                
                // 添加星形边框背景
                const starBorder = new Rect({
                    x: 0,
                    y: 0,
                    width: width,
                    height: height,
                    cornerRadius: 20,
                    stroke: '#fbbf2480',
                    strokeWidth: 7
                });
                bar.current = starBorder;
                container.addAfter(bar.current, box);
                
                // 创建星星图层的背景效果
                const starBackground = new Rect({
                    x: 0,
                    y: 0,
                    width: width,
                    height: height,
                    cornerRadius: 20,
                    fill: {
                        type: 'radialGradient',
                        stops: [
                            { offset: 0, color: '#fef3c770' },
                            { offset: 0.7, color: '#ffffff00' }
                        ],
                        cx: width / 2,
                        cy: height / 2,
                        r: Math.max(width, height) / 1.5
                    }
                });
                decorationElements.current.push(starBackground);
                container.addAfter(starBackground, box);
                
                // 添加更生动、更有层次的星星装饰
                const stars = [
                    // 四角大星星
                    { x: -18, y: -18, text: '⭐', size: 38, color: '#fbbf24', rotation: -15, glow: true },
                    { x: width - 18, y: -18, text: '⭐', size: 38, color: '#fbbf24', rotation: 15, glow: true },
                    { x: -18, y: height - 18, text: '⭐', size: 38, color: '#fbbf24', rotation: -15, glow: true },
                    { x: width - 18, y: height - 18, text: '⭐', size: 38, color: '#fbbf24', rotation: 15, glow: true },
                    
                    // 边缘闪光
                    { x: width / 2 - 12, y: -22, text: '✨', size: 32, color: '#fbbf24', rotation: 0, glow: true },
                    { x: width / 2 - 12, y: height - 10, text: '✨', size: 32, color: '#fbbf24', rotation: 0, glow: true },
                    { x: -22, y: height / 2 - 12, text: '✨', size: 32, color: '#fbbf24', rotation: 0, glow: true },
                    { x: width - 10, y: height / 2 - 12, text: '✨', size: 32, color: '#fbbf24', rotation: 0, glow: true },
                    
                    // 中等星星装饰
                    { x: width / 4, y: -10, text: '⭐', size: 28, color: '#fbbf24', rotation: 10 },
                    { x: width * 3/4, y: -10, text: '⭐', size: 28, color: '#fbbf24', rotation: -10 },
                    { x: width / 4, y: height - 18, text: '⭐', size: 28, color: '#fbbf24', rotation: 10 },
                    { x: width * 3/4, y: height - 18, text: '⭐', size: 28, color: '#fbbf24', rotation: -10 },
                    
                    // 随机散布的闪光
                    ...Array.from({ length: 15 }, () => ({
                        x: Math.random() * width,
                        y: Math.random() * height,
                        text: Math.random() < 0.3 ? '⭐' : '✨',
                        size: 8 + Math.random() * 12,
                        color: Math.random() < 0.7 ? '#fcd34d' : '#fbbf24',
                        rotation: Math.random() * 360,
                        opacity: 0.5 + Math.random() * 0.5
                    }))
                ];
                
                stars.forEach(star => {
                    const starElem = new Rect({
                        x: star.x,
                        y: star.y,
                        width: star.size,
                        height: star.size,
                        fill: 'transparent',
                        cornerRadius: 0,
                        rotation: star.rotation,
                        text: {
                            text: star.text,
                            fill: star.color + (star.opacity ? Math.floor(star.opacity * 255).toString(16).padStart(2, '0') : ''),
                            fontSize: star.size,
                            align: 'center',
                            baseline: 'middle'
                        }
                    });
                    
                    // 添加发光效果
                    if (star.glow) {
                        starElem.shadowColor = star.color;
                        starElem.shadowBlur = 15;
                        starElem.shadowOpacity = 0.6;
                    }
                    
                    // 将元素添加到装饰数组中
                    decorationElements.current.push(starElem);
                    container.addAfter(starElem, box);
                });
                
                // 增加内边距
                boxWidth = width * 0.82;
                boxHeight = height * 0.82;
                boxX = (width - boxWidth) / 2;
                boxY = (height - boxHeight) / 2;
                break;
            case 'rainbowBorder':
                container.strokeWidth = null; // 移除原有的描边
                container.stroke = null;
                container.fill = '#ffffff'; // 确保背景色
                container.cornerRadius = 24; // 更大的圆角效果
                
                // 创建彩虹背景层 - 修改这个元素的位置，确保它在图片层下方
                const rainbowBg = new Rect({
                    x: -10,
                    y: -10,
                    width: width + 20,
                    height: height + 20,
                    cornerRadius: 30,
                    shadowColor: '#00000030',
                    shadowBlur: 10,
                    shadowOffsetX: 3,
                    shadowOffsetY: 3,
                    fill: {
                        type: 'linearGradient',
                        stops: [
                            { offset: 0, color: '#ff3838' },
                            { offset: 0.17, color: '#ff9d2f' },
                            { offset: 0.33, color: '#ffdf38' },
                            { offset: 0.5, color: '#38ff38' },
                            { offset: 0.67, color: '#38cfff' },
                            { offset: 0.83, color: '#3838ff' },
                            { offset: 1, color: '#ff38ff' }
                        ],
                        x1: 0,
                        y1: 0,
                        x2: width + 20,
                        y2: height + 20
                    }
                });
                bar.current = rainbowBg;
                
                // 关键修改：将彩虹背景添加到box之前，确保图片在最上层
                container.addBefore(bar.current, box);
                
                // 创建白色内层以形成彩虹边框效果
                const innerWhite = new Rect({
                    x: 8,
                    y: 8,
                    width: width - 16,
                    height: height - 16,
                    cornerRadius: 20,
                    fill: '#ffffff',
                });
                decorationElements.current.push(innerWhite);
                container.addBefore(innerWhite, box);
                
                // 添加彩虹装饰元素
                const rainbowColors = [
                    '#ff3838', '#ff9d2f', '#ffdf38', '#38ff38', 
                    '#38cfff', '#3838ff', '#ff38ff'
                ];
                
                // 添加小彩虹装饰元素
                const decorations = [];
                
                // 四角装饰
                decorations.push(
                    { x: -5, y: -5, size: 20, shape: 'circle', color: rainbowColors[0] },
                    { x: width - 15, y: -5, size: 20, shape: 'circle', color: rainbowColors[2] },
                    { x: -5, y: height - 15, size: 20, shape: 'circle', color: rainbowColors[4] },
                    { x: width - 15, y: height - 15, size: 20, shape: 'circle', color: rainbowColors[6] }
                );
                
                // 边缘装饰
                for (let i = 0; i < 7; i++) {
                    const pos = i / 6;
                    decorations.push(
                        { 
                            x: width * pos - 5, 
                            y: -5, 
                            size: 10 + Math.random() * 5, 
                            shape: Math.random() > 0.5 ? 'circle' : 'star', 
                            color: rainbowColors[i % 7],
                            opacity: 0.7 + Math.random() * 0.3
                        },
                        { 
                            x: width * pos - 5, 
                            y: height - 5, 
                            size: 10 + Math.random() * 5, 
                            shape: Math.random() > 0.5 ? 'circle' : 'star', 
                            color: rainbowColors[(i + 3) % 7],
                            opacity: 0.7 + Math.random() * 0.3
                        },
                        { 
                            x: -5, 
                            y: height * pos - 5, 
                            size: 10 + Math.random() * 5, 
                            shape: Math.random() > 0.5 ? 'circle' : 'star', 
                            color: rainbowColors[(i + 1) % 7],
                            opacity: 0.7 + Math.random() * 0.3
                        },
                        { 
                            x: width - 5, 
                            y: height * pos - 5, 
                            size: 10 + Math.random() * 5, 
                            shape: Math.random() > 0.5 ? 'circle' : 'star', 
                            color: rainbowColors[(i + 5) % 7],
                            opacity: 0.7 + Math.random() * 0.3
                        }
                    );
                }
                
                decorations.forEach(deco => {
                    let decoElem;
                    
                    if (deco.shape === 'circle') {
                        decoElem = new Rect({
                            x: deco.x,
                            y: deco.y,
                            width: deco.size,
                            height: deco.size,
                            cornerRadius: deco.size / 2,
                            fill: deco.color + (deco.opacity ? Math.floor(deco.opacity * 255).toString(16).padStart(2, '0') : 'e0'),
                            shadowColor: deco.color,
                            shadowBlur: 5,
                            shadowOpacity: 0.3
                        });
                    } else {
                        decoElem = new Rect({
                            x: deco.x,
                            y: deco.y,
                            width: deco.size,
                            height: deco.size,
                            fill: 'transparent',
                            text: {
                                text: '✨',
                                fill: deco.color + (deco.opacity ? Math.floor(deco.opacity * 255).toString(16).padStart(2, '0') : 'e0'),
                                fontSize: deco.size,
                                align: 'center',
                                baseline: 'middle'
                            },
                            shadowColor: deco.color,
                            shadowBlur: 5,
                            shadowOpacity: 0.3
                        });
                    }
                    
                    // 将元素添加到装饰数组中
                    decorationElements.current.push(decoElem);
                    
                    // 关键修改：将装饰元素添加到box之前，确保图片在最上层
                    container.addBefore(decoElem, box);
                });
                
                // 增加内边距
                boxWidth = width * 0.85;
                boxHeight = height * 0.85;
                boxX = (width - boxWidth) / 2;
                boxY = (height - boxHeight) / 2;
                break;
            default:
                // 处理基础框架
                    container.strokeWidth = null;
                    container.stroke = null;
                break;
        }
        
        // 设置容器位置和尺寸
        const { x, y } = getPosition(align, frameConf.width - width, frameConf.height - totalHeight);
        container.width = width;
        container.height = totalHeight;
        container.origin = align;
        container.x = x;
        container.y = y;
        box.width = boxWidth;
        box.height = boxHeight;
        box.x = boxX;
        box.y = boxY;
        
        // 计算图像尺寸
        const imgAspectRatio = img.width / img.height;
        const boxAspectRatio = boxWidth / boxHeight;
        
        let imageWidth, imageHeight;
        
        // 根据图片宽高比适配到框架中
            if (imgAspectRatio > boxAspectRatio) {
            // 图片更宽，以框架宽度为基准
            imageWidth = boxWidth - padding * 2;
            imageHeight = imageWidth / imgAspectRatio;
        } else {
            // 图片更高，以框架高度为基准
            imageHeight = boxHeight - padding * 2;
            imageWidth = imageHeight * imgAspectRatio;
        }
        
        // 设置图片位置和尺寸
        image.width = imageWidth + 2; // 解决有缝隙的问题
        image.height = imageHeight + 2;
        image.x = (boxWidth - imageWidth) / 2 - 1; // 居中显示
        image.y = (boxHeight - imageHeight) / 2 - 1;
        
        // 应用图像填充
        image.fill = {
            type: 'image',
            url: filteredImageCache.current || stores.editor.img.src,
            align: 'center',
            mode: stores.option.mode
        };
        
        createSnap();
        return (() => {
            container.strokeWidth = null;
            container.stroke = null;
            if (bar.current) {
                bar.current.remove();
            bar.current = null;
            }
            
            // 使用辅助函数清理所有装饰元素
            clearDecorations();
            
            box.cornerRadius = null;
            image.cornerRadius = round;
            container.cornerRadius = round;
            container.shadow = {
                x: shadow * 4,
                y: shadow * 4,
                blur: shadow * 3,
                color: '#00000045',
                box: true
            };
        });
    }, [stores.option.frameConf.width, stores.option.frameConf.height, stores.option.padding, stores.option.align, stores.option.frame]);

    useEffect(() => {
        parent.add(container);
        return (() => {
            // 组件卸载时，确保清理所有资源
            clearDecorations();
            container.remove();
        })
    }, [parent]);
    
    return loading ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white/30 z-10">
            <Spin tip={t('loading.text')} />
        </div>
    ) : null;
});